'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, Link2, Users } from 'lucide-react';
import type { CollabMember } from '@/components/datahunter/use-collab';

/**
 * Панель совместной работы: кто в документе и кому разрешено править.
 *
 * Правами распоряжается только тот, кто открыл сессию. У остальных
 * переключателей здесь нет вовсе, а не «есть, но не работают»: показывать
 * человеку органы управления, которые ему не принадлежат, значит обманывать.
 */
export function CollabPanel({
  roomId,
  connected,
  isOwner,
  canEdit,
  openEditing,
  members,
  me,
  onShare,
  onToggleOpenEditing,
  onMemberAccess,
}: {
  roomId: string | null;
  connected: boolean;
  isOwner: boolean;
  canEdit: boolean;
  openEditing: boolean;
  members: CollabMember[];
  me: string;
  onShare: () => Promise<void>;
  onToggleOpenEditing: (value: boolean) => void;
  onMemberAccess: (member: string, allow: boolean) => void;
}) {
  const t = useTranslations('dh_collab');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const start = () => {
    setBusy(true);
    void onShare().finally(() => {
      setBusy(false);
    });
  };

  const copyLink = () => {
    if (!roomId) return;

    const url = `${window.location.origin}${window.location.pathname}?room=${roomId}`;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    });
  };

  if (!roomId) {
    return (
      <button
        onClick={start}
        disabled={busy}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm hover:border-[var(--color-accent-500)] transition-colors disabled:opacity-60"
      >
        <Users className="w-4 h-4" />
        {busy ? t('sharing') : t('share')}
      </button>
    );
  }

  return (
    <div
      className="border border-[var(--color-border)] rounded-xl p-4 bg-[var(--color-bg-card)]"
      // Идентификатор комнаты нужен и человеку (через кнопку «скопировать
      // ссылку»), и автотестам, которым иначе пришлось бы читать состояние
      // React.
      data-room-id={roomId}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Users className="w-4 h-4" />
          {t('title')}
          <span
            className={`text-xs font-normal ${
              connected ? 'text-[var(--color-accent-400)]' : 'text-[var(--color-text-muted)]'
            }`}
          >
            {connected ? t('connected') : t('offline')}
          </span>
        </h2>

        <button
          onClick={copyLink}
          className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-accent-500)] transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
          {copied ? t('link_copied') : t('copy_link')}
        </button>
      </div>

      {isOwner ? (
        <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={openEditing}
            onChange={(e) => {
              onToggleOpenEditing(e.target.checked);
            }}
            className="accent-[var(--color-brand-500)]"
          />
          {t('open_editing')}
        </label>
      ) : (
        !canEdit && (
          <p className="text-sm text-[var(--color-text-muted)] mb-3">{t('read_only_notice')}</p>
        )
      )}

      <h3 className="text-xs uppercase tracking-wide text-[var(--color-text-muted)] mb-2">
        {t('members')}
      </h3>
      <ul className="space-y-1.5">
        {members.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate">
              {m.name || t('guest')}
              <span className="text-[var(--color-text-muted)]">
                {m.id === me ? ` (${t('you')})` : ''}
                {m.isOwner ? ` (${t('owner')})` : ''}
              </span>
            </span>

            <span className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-[var(--color-text-muted)]">
                {m.canEdit ? t('can_edit') : t('read_only')}
              </span>

              {/* Владельцу право правки не выдают и не отнимают: документ его. */}
              {isOwner && !m.isOwner && !openEditing && (
                <button
                  onClick={() => {
                    onMemberAccess(m.id, !m.canEdit);
                  }}
                  className="text-xs px-2 py-0.5 rounded border border-[var(--color-border)] hover:border-[var(--color-accent-500)] transition-colors"
                >
                  {m.canEdit ? t('revoke') : t('allow')}
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>

      <p className="mt-4 text-xs text-[var(--color-text-muted)]/80 leading-relaxed">{t('note')}</p>
    </div>
  );
}
